import { useEffect, useState } from 'react';
import { useAppContext } from '@/Main';
import { useBrandLogoHost } from '../context/BrandLogoContext';
import { brandInitial, BRAND_COLORS } from '../utils/format';
import { faviconUrl } from '../utils/favicon';

interface BrandFaviconProps {
  host?: string | null;
  faviconUrl?: string | null;
  name?: string;
  /** When set to you/own_brand, falls back to the project's primary domain. */
  relationship?: string | null;
  brandType?: string | null;
  size?: 'xs' | 'sm' | 'md';
  colorIndex?: number;
  className?: string;
  /** Logo only, no filled tile behind it. */
  plain?: boolean;
  /** Alias used by some call sites; maps to host. */
  domain?: string | null;
}

const SIZE_PX: Record<NonNullable<BrandFaviconProps['size']>, number> = {
  xs: 16,
  sm: 28,
  md: 36
};

/**
 * Single place for brand logos across the panel.
 * Prefers an explicit faviconUrl/host, then falls back to the project
 * brand-host registry (domains, aliases, competitors, own brand).
 */
export function BrandFavicon({
  host,
  domain,
  faviconUrl: faviconUrlProp,
  name = '?',
  relationship,
  brandType,
  size = 'sm',
  colorIndex = 0,
  className = '',
  plain = false
}: BrandFaviconProps) {
  const resolvedHost = useBrandLogoHost(name, host ?? domain, {
    relationship,
    brandType
  });
  const candidates = [faviconUrlProp, faviconUrl(resolvedHost)].filter(
    (value, index, arr): value is string =>
      Boolean(value) && arr.indexOf(value) === index
  );
  const [attempt, setAttempt] = useState(0);
  const url = candidates[attempt] ?? null;
  const px = SIZE_PX[size];
  const fontSize = size === 'xs' ? 9 : size === 'sm' ? 12 : 14;
  const {
    config: { themeVariant },
  } = useAppContext() as { config: { themeVariant?: string } };
  const bgClass = BRAND_COLORS[colorIndex % BRAND_COLORS.length];
  const lightPrimaryLetter = themeVariant === 'primary' && bgClass === 'bg-primary';

  useEffect(() => {
    setAttempt(0);
  }, [faviconUrlProp, resolvedHost]);

  if (!url) {
    return (
      <span
        className={`${plain ? 'text-700' : `${bgClass} ${lightPrimaryLetter ? '' : 'text-white'}`} fw-bold d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-2 ${className}`}
        style={{
          width: px,
          height: px,
          fontSize,
          minWidth: px,
          minHeight: px,
          ...(!plain && lightPrimaryLetter ? { color: 'var(--falcon-primary-text-emphasis)' } : {}),
        }}
      >
        {brandInitial(name)}
      </span>
    );
  }

  return (
    <span
      className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden rounded-2 ${plain ? '' : 'bg-200'} ${className}`}
      style={{ width: px, height: px, minWidth: px, minHeight: px }}
    >
      <img
        src={url}
        alt=""
        width={px}
        height={px}
        className="d-block"
        style={{ width: px, height: px, objectFit: 'contain' }}
        onError={() => {
          setAttempt(current => current + 1);
        }}
      />
    </span>
  );
}
